import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, errText } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, CheckCircle2, User } from "lucide-react";
import { celebrate } from "@/lib/celebrate";

export default function StudentLogin() {
  const [step, setStep] = useState(1);
  const [code, setCode] = useState("");
  const [school, setSchool] = useState(null);
  const [classes, setClasses] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [classId, setClassId] = useState("");
  const [division, setDivision] = useState("");
  const [roll, setRoll] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { loginWithToken } = useAuth();
  const navigate = useNavigate();

  const className = classes.find((c) => c.id === classId)?.name || "";

  const verify = async (e) => {
    e.preventDefault(); setLoading(true); setError("");
    try {
      const { data } = await api.post("/student/verify-school", { school_code: code });
      setSchool(data.school); setClasses(data.classes); setStep(2);
    } catch (err) { setError(errText(err)); } finally { setLoading(false); }
  };

  const pickClass = async (id) => {
    setClassId(id); setDivision("");
    try {
      const { data } = await api.get("/student/divisions", { params: { school_code: code, class_id: id } });
      setDivisions(data);
    } catch { setDivisions([]); }
  };

  // Step 2 -> find the student by School ID + Class + Division + Roll No
  const findStudent = async (e) => {
    e.preventDefault(); setLoading(true); setError("");
    try {
      const { data } = await api.post("/student/lookup", {
        school_code: code, class_id: classId, division, roll_no: Number(roll),
      });
      setName(data.name);
      setStep(3);
    } catch (err) { setError(errText(err)); } finally { setLoading(false); }
  };

  // Step 3 -> confirm and log in
  const login = async () => {
    setLoading(true); setError("");
    try {
      const { data } = await api.post("/student/login", {
        school_code: code, class_id: classId, division, roll_no: Number(roll),
      });
      celebrate();
      loginWithToken(data.access_token, { role: "student", student: data.student });
      setTimeout(() => navigate("/student"), 900);
    } catch (err) { setError(errText(err)); setStep(2); } finally { setLoading(false); }
  };

  const stepLabel =
    step === 1 ? "Step 1 — Enter your School ID"
    : step === 2 ? `Step 2 — ${school?.name}`
    : `Step 3 — Confirm your details`;

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-lg">
        <Link to="/" className="text-sm text-slate-500 inline-flex items-center gap-1 mb-6" data-testid="back-home">
          <ArrowLeft className="h-4 w-4" /> Back
        </Link>
        <div className="soft-card p-8 rounded-3xl">
          <img src="/gdiot-logo-wide.png" alt="CT Skill Assessment" className="h-16 w-auto object-contain mx-auto" data-testid="app-logo" />
          <h2 className="text-2xl font-head font-bold mt-5">Student Sign In</h2>
          <p className="text-sm text-slate-500 mt-1">{stepLabel}</p>

          {step === 1 && (
            <form onSubmit={verify} className="mt-7 space-y-4">
              <div>
                <Label htmlFor="code">School ID</Label>
                <Input id="code" required value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="e.g. SUNR123" className="mt-1.5 tracking-widest" data-testid="school-code-input" />
              </div>
              {error && <p className="text-sm text-rose-600" data-testid="student-login-error">{error}</p>}
              <Button type="submit" className="w-full rounded-full" disabled={loading} data-testid="verify-school-btn">
                {loading ? "Checking…" : "Continue"}
              </Button>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={findStudent} className="mt-7 space-y-5">
              <div>
                <Label>Class</Label>
                <div className="flex flex-wrap gap-2 mt-2" data-testid="class-picker">
                  {classes.map((c) => (
                    <button type="button" key={c.id} onClick={() => pickClass(c.id)}
                      data-testid={`class-option-${c.name.replace(/\s+/g, "-").toLowerCase()}`}
                      className={`px-4 py-2 rounded-full text-sm border transition-colors ${classId === c.id ? "bg-primary text-white border-primary" : "bg-white border-slate-200 hover:bg-slate-50"}`}>
                      {c.name}
                    </button>
                  ))}
                </div>
              </div>
              {classId && (
                <div>
                  <Label>Division</Label>
                  <div className="flex flex-wrap gap-2 mt-2" data-testid="division-picker">
                    {divisions.length === 0 && <p className="text-sm text-slate-500">No divisions found for this class.</p>}
                    {divisions.map((d) => (
                      <button type="button" key={d.id} onClick={() => setDivision(d.name)}
                        data-testid={`division-option-${d.name.toLowerCase()}`}
                        className={`px-4 py-2 rounded-full text-sm border transition-colors ${division === d.name ? "bg-primary text-white border-primary" : "bg-white border-slate-200 hover:bg-slate-50"}`}>
                        {d.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <Label htmlFor="roll">Roll Number</Label>
                <Input id="roll" type="number" required value={roll} onChange={(e) => setRoll(e.target.value)}
                  className="mt-1.5" placeholder="e.g. 23" data-testid="roll-input" />
              </div>
              {error && <p className="text-sm text-rose-600" data-testid="student-login-error">{error}</p>}
              <Button type="submit" className="w-full rounded-full" disabled={loading || !classId || !division || !roll}
                data-testid="find-student-btn">
                {loading ? "Finding you…" : "Find Me"}
              </Button>
              <button type="button" className="text-sm text-slate-500 w-full" onClick={() => { setStep(1); setError(""); }}
                data-testid="change-school-btn">Change School ID</button>
            </form>
          )}

          {step === 3 && (
            <div className="mt-7 space-y-5">
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50/70 p-5 text-center" data-testid="student-confirm-card">
                <div className="mx-auto h-11 w-11 rounded-full bg-white flex items-center justify-center shadow-sm">
                  <User className="h-5 w-5 text-emerald-600" />
                </div>
                <p className="text-xs uppercase tracking-wide text-emerald-700 mt-3">Student Name</p>
                <p className="text-2xl font-head font-bold text-slate-900 mt-1" data-testid="fetched-student-name">{name}</p>
                <div className="grid grid-cols-3 gap-2 mt-4 text-sm">
                  <div className="rounded-xl bg-white py-2">
                    <p className="text-slate-400 text-xs">Class</p>
                    <p className="font-semibold">{className}</p>
                  </div>
                  <div className="rounded-xl bg-white py-2">
                    <p className="text-slate-400 text-xs">Division</p>
                    <p className="font-semibold">{division}</p>
                  </div>
                  <div className="rounded-xl bg-white py-2">
                    <p className="text-slate-400 text-xs">Roll No.</p>
                    <p className="font-semibold">{roll}</p>
                  </div>
                </div>
              </div>
              <p className="text-sm text-slate-500 text-center">Is this you? Confirm to open your dashboard.</p>
              {error && <p className="text-sm text-rose-600 text-center" data-testid="student-login-error">{error}</p>}
              <Button onClick={login} className="w-full rounded-full" disabled={loading} data-testid="confirm-student-btn">
                <CheckCircle2 className="h-4 w-4 mr-1.5" />
                {loading ? "Opening…" : "Yes, that's me — Open Dashboard"}
              </Button>
              <button type="button" className="text-sm text-slate-500 w-full" onClick={() => { setStep(2); setName(""); setError(""); }}
                data-testid="not-me-btn">Not you? Go back</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
